import React, { useCallback, useMemo, useState } from 'react'
import { CardRowLine } from '@broker-crm-common'
import { TaskRowSidebarDraw } from '@broker-crm-common/ReviewModeDrawer/Tasks/Tasks.styles'
import { TrackEventParams } from '@jonkoops/matomo-tracker-react/lib/types'
import { Box, useTheme } from '@mui/material'
import { Location } from 'history'
import { FormattedMessage } from 'react-intl'

import { testHandle } from '@acre/utils'
import { Maybe, MortgageReason } from '@acre/graphql'
import { Button, BUTTON_PRIMARY, BUTTON_SECONDARY, Modal, trackEventFiltered } from '@acre/design-system'

import { useUpdateCaseStatus } from '../../../../../utils/hooks/useUpdateCaseStatus'
import { TasksLocation } from '../CaseOverview.helpers'
import { ChevronIcon, getTaskTextByStatus, validTransitionStatusType } from './CaseVerifications.helpers'
import { VerificationTaskButton } from '../CaseOverview.styles'

type Props = {
  status: validTransitionStatusType
  caseId: string
  preferenceMortgageReason: Maybe<MortgageReason>
  onSubmit?: () => any
  tasksLocation?: TasksLocation
  trackEvent?: (params: TrackEventParams) => void | undefined
  location?: Location<unknown>
}

export const StatusTransitionTask = ({
  status,
  caseId,
  preferenceMortgageReason,
  onSubmit,
  tasksLocation,
  trackEvent,
  location,
}: Props) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const handleClose = () => setModalOpen(false)

  const theme = useTheme()

  const [updateCaseStatus] = useUpdateCaseStatus(caseId)

  const handleSubmit = useCallback(async () => {
    setIsLoading(true)

    await updateCaseStatus({
      variables: {
        id: caseId,
        input: {
          update: status,
        },
      },
    })

    onSubmit && (await onSubmit())
    setIsLoading(false)
    handleClose()
  }, [caseId, onSubmit, status, updateCaseStatus])

  const { taskName, modalMessage } = getTaskTextByStatus(status, preferenceMortgageReason)

  const buttonContent = useMemo(() => {
    return (
      <VerificationTaskButton type="button" id={`CaseTransitionTo${status}`}>
        <FormattedMessage id={taskName} />
        {ChevronIcon}
      </VerificationTaskButton>
    )
  }, [status, taskName])

  const handleClick = useCallback(() => {
    setModalOpen(true)
    trackEvent && trackEventFiltered(trackEvent, location, 'taskClick', `location-${tasksLocation}`)
  }, [location, tasksLocation, trackEvent])

  const renderButton = useMemo(() => {
    if (tasksLocation === TasksLocation.CaseSidebarDrawer) {
      return <TaskRowSidebarDraw onClick={handleClick}>{buttonContent}</TaskRowSidebarDraw>
    } else {
      return (
        <CardRowLine
          style={{ display: 'flex' }}
          onClick={handleClick}
          data-testid={testHandle(`CaseTransitionTo${status}`)}
        >
          {buttonContent}
        </CardRowLine>
      )
    }
  }, [buttonContent, handleClick, status, tasksLocation])

  return (
    <>
      {renderButton}
      <Modal
        id="ConfirmCaseTransitionModal"
        open={isModalOpen}
        onClose={handleClose}
        showHeading={false}
        footer={
          <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%">
            <Button id="Cancel" buttonStyle={BUTTON_SECONDARY} onClick={handleClose}>
              <FormattedMessage id="generic.cancel" />
            </Button>
            <Box ml={theme.spacers.size16}>
              <Button
                data-testid={`ConfirmCaseTransitionTo${status}`}
                id={`ConfirmCaseTransitionTo${status}`}
                buttonStyle={BUTTON_PRIMARY}
                isLoading={isLoading}
                onClick={handleSubmit}
              >
                <FormattedMessage id="generic.confirm" />
              </Button>
            </Box>
          </Box>
        }
      >
        <FormattedMessage id={modalMessage} />
      </Modal>
    </>
  )
}
